import { BackdropLoader } from '@/Components/Loaders';
import VariantTable from '@/Pages/Food Products/VariantPrice/VariantTable';
import {
	COLUMN_MAPPING,
	getColumnsForTab,
	getEmptyMessageForTab,
} from '../annotationProductivity.config';

const alignmentClass = {
	left: 'justify-start text-left',
	center: 'justify-center text-center',
};

const formatInt = (value) => (value ?? 0).toLocaleString('en-IN');

const renderAlignedCell = (content, align = 'left') => {
	const alignment = alignmentClass[align] ?? alignmentClass.left;
	return (
		<div className={`flex min-h-[1.75rem] w-full items-center ${alignment}`}>
			{content}
		</div>
	);
};

const ProductivityTable = ({ activeTab, rows, loading, loadFailed, accessDenied }) => {
	const cellRenderers = {
		// VariantTable sorts internally and hands back the row's index in the
		// SORTED data. Deriving it from the unsorted `rows` prop would scramble
		// S.No the moment any column is sorted.
		serial: (row, index) => renderAlignedCell((index ?? 0) + 1, 'center'),
		user: (row) => renderAlignedCell(
			<span className='font-medium text-slate-800'>{row?.user ?? '-'}</span>,
			'left'
		),
		customer: (row) => renderAlignedCell(row?.customer ?? '-', 'left'),
		imagesAllocated: (row) => renderAlignedCell(formatInt(row?.imagesAllocated), 'center'),
		imagesAnnotated: (row) => renderAlignedCell(formatInt(row?.imagesAnnotated), 'center'),
		percentCompleted: (row) => {
			if (row?.percentCompleted === null) {
				return renderAlignedCell(<span className='text-slate-400'>—</span>, 'center');
			}
			// Static text, uncapped: sorting uses the raw ratio, so capping would
			// rank a 145% row above a genuine 100% one while both read "100%".
			// Above 100% means the allocation shrank after completion — the reader
			// can see that in the Allocated and Annotated columns on this same row,
			// so it needs no per-cell explanation. Floor, so 99.6% is not "100%".
			const percent = `${Math.floor(row.percentCompleted)}%`;
			return renderAlignedCell(
				<span className='text-slate-700'>{percent}</span>,
				'center'
			);
		},
		shapesDrawn: (row) => renderAlignedCell(formatInt(row?.shapesDrawn), 'center'),
		productIdsAnnotated: (row) => renderAlignedCell(
			formatInt(row?.productIdsAnnotated),
			'center'
		),
		productIdsReviewed: (row) => renderAlignedCell(
			formatInt(row?.productIdsReviewed),
			'center'
		),
	};

	let emptyMessage = getEmptyMessageForTab(activeTab);
	if (accessDenied) {
		emptyMessage = 'This dashboard is internal-only — your account does not have access to it.';
	} else if (loadFailed) {
		emptyMessage = 'Could not load productivity data. Try again in a moment.';
	}

	return (
		<div className='relative min-h-[22rem] flex-1 overflow-hidden rounded-lg border border-slate-200 bg-white'>
			<BackdropLoader isLoading={loading} showProgress />
			<VariantTable
				colMapping={COLUMN_MAPPING}
				columns={getColumnsForTab(activeTab)}
				data={rows}
				customRowData={cellRenderers}
				customInfoLine={emptyMessage}
			/>
		</div>
	);
};

export default ProductivityTable;
